<script>
import FloorplanLink from '@/components/floorplan/floorplan-link.js'
import {ExhibitorMixin} from "../lib/speaker-utils.js"
import Favorite from "../lib/favorite.vue"

export default {
  mixins: [ExhibitorMixin, FloorplanLink],
  components: {Favorite},
  props: {
    exhibitorType: {type: String, default: 'exhibitor'},
    exhibitor: {type: Object, required: true},
    listDisplay: {type: Boolean, default: true},
  },

  data() {
    return {
      descriptionVisible: false,
    }
  },

  computed: {
    fallbackLogo() {
      return (process.env.BASE_URL || '/') + 'media/app/company-symbol.png'
    },
    listDescription() {
      if (!this.exhibitor.description) return ''
      const plain = this.exhibitor.description.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
      return plain.length > 500 ? plain.slice(0, 500) + '…' : plain
    },
  },

  methods: {
    showOnFloorplan() {
      this.openFloorplan(this.exhibitor.stand)
    },

    website(url) {
      if (!url) return url
      if (url.startsWith('http')) return url
      return 'https://' + url
    },
  },
}
</script>

<template>
    <div class="row">
          <div class="col-3 col-lg-2">
            <div class="ratio ratio-1x1 border rounded bg-light mb-2">
              <img class="object-fit-contain p-1"
                   :src="exhibitor.logo || fallbackLogo"
                   :alt="exhibitor.company_name"
                   @error="$event.target.onerror = null; $event.target.src = fallbackLogo">
            </div>
          </div>
          <div class="col-9 col-lg-10">
            <div class="row">
              <div class="col-12 mb-3">
                <div class="mb-2 lead">
                  <router-link v-if="listDisplay" :to="exhibitorUrl(exhibitor)">
                    {{ exhibitor.company_name }}
                  </router-link>
                  <span v-else>{{ exhibitor.company_name }}</span>
                  &nbsp;
                  <favorite :attr="exhibitorType" :value="exhibitor.sfid" :obj="exhibitor"
                   @favorite-changed="$emit('favorite-changed', $event)" />
                </div>

                <p v-if="exhibitor.slogan" class="text-primary fw-bold mb-2 text-break">
                  {{ exhibitor.slogan }}
                </p>

                <!-- Categories: ab md unterhalb des Slogans -->
                <div v-if="exhibitor.categories && exhibitor.categories.length > 0"
                     class="mb-2 d-none d-md-block">
                  <span v-for="cat in exhibitor.categories" :key="cat.id"
                        class="badge bg-secondary me-1 mb-1 category-badge badge-wrap"
                        @click.prevent="$emit('category-clicked', cat)">
                    {{ cat.name }}
                  </span>
                </div>

                <p v-if="exhibitor.address">{{ exhibitor.address }}</p>

                <p v-if="exhibitor.hall || exhibitor.stand" class="text-muted mb-2">
                  <span v-if="exhibitor.hall">Halle: {{ exhibitor.hall }}</span>
                  <span v-if="exhibitor.hall && exhibitor.stand"> | </span>
                  <!-- The stand number is the link to the map. A separate button
                       would compete with it: the number is what people look for. -->
                  <a v-if="exhibitor.stand" href="#" @click.prevent="showOnFloorplan">
                    Stand: {{ exhibitor.stand }}
                    <i class="bi bi-geo-alt" aria-hidden="true"></i>
                  </a>
                </p>

                <div v-if="exhibitor.type != 'Exhibitor'" class="c-title pb-2">
                  <i class="fas fa-trophy"></i>&nbsp;{{ exhibitor.type }}
                </div>

                <div v-if="!listDisplay" class="mt-2">
                  <a v-if="exhibitor.phone" :href="'tel:' + exhibitor.phone"
                     class="btn btn-sm btn-outline-primary me-2 mb-2">
                    <i class="bi bi-telephone"></i> Anrufen
                  </a>
                  <a v-if="exhibitor.website" :href="website(exhibitor.website)" target="_blank"
                     class="btn btn-sm btn-outline-primary mb-2">
                    <i class="bi bi-globe"></i> Website
                  </a>
                </div>
              </div>

              <div v-if="listDescription && listDisplay"
               class="col col-12 mb-3 d-none d-lg-block">
                {{ listDescription }}
              </div>
            </div>
          </div>

          <!-- List-View mobile: aufklappbare Description + Kategorien darunter -->
          <div v-if="listDescription && listDisplay" class="col col-12 pt-3 d-lg-none">
            <a href="#" class="btn btn-outline float-end"
            @click.prevent="descriptionVisible=!descriptionVisible">
              <i v-if="descriptionVisible" class="fa fa-chevron-down"></i>
              <i v-else class="fa fa-chevron-right"></i>
            </a>
            <p v-if="descriptionVisible">{{ listDescription }}</p>
            <p v-else class="c-truncate">{{ listDescription }}</p>
          </div>

          <!-- Detail-View description -->
          <div v-if="exhibitor.description && !listDisplay" class="col col-12 pt-3"
            v-html="exhibitor.description">
          </div>

          <!-- Categories: auf mobil (< md) unterhalb der Description -->
          <div v-if="exhibitor.categories && exhibitor.categories.length > 0"
               class="col col-12 mt-2 d-md-none">
            <span v-for="cat in exhibitor.categories" :key="cat.id"
                  class="badge bg-secondary me-1 mb-1 category-badge"
                  @click.prevent="$emit('category-clicked', cat)">
              {{ cat.name }}
            </span>
          </div>

      <slot></slot>
    </div>
</template>

<style scoped>

.c-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.c-session {
  border-top: 1px solid lightgrey;
}

hr {
  border-color: lightgrey;
}

.avatar {
  height:100px;
  width:100px;
  object-fit:cover;
  border-radius:50%;
  overflow:hidden;
}

.category-badge {
  cursor: pointer;
}
.category-badge:hover {
  opacity: 0.8;
}

</style>
